"use client";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Smart navigation cleanup hook
 * Only cancels queries when there's a legitimate navigation issue
 * Preserves queries during normal middleware redirects
 */
export function useNavigationCleanup() {
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const previousPathname = useRef(pathname);
  const navigationTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Track pathname changes
    if (previousPathname.current !== pathname) {
      console.log(`Navigation: ${previousPathname.current} → ${pathname}`);

      // Clear any existing timeout
      if (navigationTimeout.current) {
        clearTimeout(navigationTimeout.current);
      }

      // Set a timeout to detect stuck queries
      // If queries are still fetching after 30 seconds, cancel them
      navigationTimeout.current = setTimeout(() => {
        const queries = queryClient.getQueryCache().getAll();
        const stuckQueries = queries.filter(
          (query) => query.state.fetchStatus === "fetching"
        );

        if (stuckQueries.length > 0) {
          console.warn(
            `Found ${stuckQueries.length} stuck queries after navigation, cancelling...`,
            stuckQueries.map((q) => q.queryKey)
          );
          queryClient.cancelQueries();
        }
      }, 30000); // 30 second timeout

      previousPathname.current = pathname;
    }

    return () => {
      if (navigationTimeout.current) {
        clearTimeout(navigationTimeout.current);
      }
    };
  }, [pathname, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeout.current) {
        clearTimeout(navigationTimeout.current);
      }
    };
  }, []);
}
